export interface MixinContext {
  app_version: string;
  immersive: boolean;
  appearance: "light" | "dark";
  currency: string;
  locale: string;
  platform: "iOS" | "Android" | "Desktop";
  conversation_id: string;
  [props: string]: string | boolean;
}

export interface Bridge {
  getContext?: () => MixinContext;
  reloadTheme?: () => void;
  playlist?: (list: string[]) => void;
  close?: () => void;
  getTipAddress?: (chainId: string, callback: string) => void;
  tipSign?: (chainId: string, message: string, callback: string) => void;
  getAssets?: (list: string[], callback: string) => void;
  signBotSignature?: (
    args: [string, boolean, string, string, string, string],
  ) => void;
}

export type IOSBridge = {
  [K in keyof Bridge]: NonNullable<Bridge[K]> extends (
    ...args: infer Args
  ) => infer Return
    ? { postMessage: (...args: Args) => Return }
    : never;
};

type BridgeMethod = keyof Bridge;

function isIOS(): boolean {
  return !!window?.webkit?.messageHandlers;
}

function isAndroid(): boolean {
  return !!window?.MixinContext;
}

export function isMixinEnvironment(): boolean {
  return isIOS() || isAndroid();
}

function call<M extends BridgeMethod>(
  method: M,
  ...args: Parameters<NonNullable<Bridge[M]>>
): ReturnType<NonNullable<Bridge[M]>> | undefined {
  if (isAndroid()) {
    const context = window.MixinContext;
    if (!context) return undefined;

    if (!context[method]) return undefined;

    let result = (
      context[method] as (
        ...args: Parameters<NonNullable<Bridge[M]>>
      ) => ReturnType<NonNullable<Bridge[M]>>
    )(...args);

    console.log("Bridge call raw result:", result, "typeof", typeof result);

    if (result && typeof result === "string") {
      result = JSON.parse(result) as ReturnType<NonNullable<Bridge[M]>>;
    }

    return result;
  } else if (isIOS()) {
    if (method === "getContext") {
      const result = prompt("MixinContext.getContext()");
      return (result ? JSON.parse(result) : undefined) as ReturnType<
        NonNullable<Bridge[M]>
      >;
    } else {
      const handlers = window.webkit?.messageHandlers;
      if (!handlers) return undefined;

      const handler = handlers[method];
      if (!handler) return undefined;

      return args.length > 0
        ? (handler.postMessage(...args) as ReturnType<NonNullable<Bridge[M]>>)
        : (handler.postMessage(null) as ReturnType<NonNullable<Bridge[M]>>);
    }
  }
  return undefined;
}

export const getContext = () => call("getContext");

export const reloadTheme = () => call("reloadTheme");

export const playlist = (list: string[]) => call("playlist", list);

export const close = () => call("close");

export const getTipAddress = (chainId: string, callback: string) => {
  call("getTipAddress", chainId, callback);
};

export const tipSign = (chainId: string, message: string, callback: string) => {
  call("tipSign", chainId, message, callback);
};

export const getAssets = (list: string[], callback: string) => {
  call("getAssets", list, callback);
};

export function signBotSignature(
  args: [string, boolean, string, string, string, string],
) {
  call("signBotSignature", args);
}
